import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaStar, FaChevronLeft, FaChevronRight, FaQuoteLeft, FaUser } from 'react-icons/fa';

const testimonials = [
  {
    name: 'Gustavo',
    company: 'Academia Tito Colo',
    image: '/images/resource/testemunha1.jpg',
    text: 'Quero parabenizar toda a equipe da Arte Verde pelo profissionalismo, competência e dedicação demonstrados durante todo o processo. Desde o planejamento até a execução, vocês foram extremamente atenciosos, cumprindo prazos e garantindo a qualidade do serviço.',
    rating: 5,
    hasPhoto: true,
  },
  {
    name: 'Denise',
    company: 'Projeto doméstico',
    image: '/images/resource/testimonial-2.jpg',
    text: 'Trabalho maravilhoso!! Atendimento super profissional, esclareceram todas as minhas dúvidas durante todo alinhamento do projeto, no final o resultado excedeu as minhas expectativas!!',
    rating: 5,
    hasPhoto: true,
  },
  {
    name: 'Luciano',
    company: 'Projeto doméstico',
    image: '/images/resource/testimonial-1.jpg',
    text: 'Atendimento sensacional, desde o momento em que solicitei orçamento até a entrega final do projeto! Recomendo demais!!!',
    rating: 5,
    hasPhoto: true,
  },
  {
    name: 'Elaine Souza',
    company: 'Cliente',
    image: '',
    text: 'Super indico. Equipe de ótimos profissionais. São comprometidos, dedicados e muito parceiros. Já fizeram vários trabalhos para nossa empresa e sempre voltamos porque são excelentes no que fazem.',
    rating: 5,
    hasPhoto: false,
  },
  {
    name: 'Shirley Kris',
    company: 'Cliente',
    image: '',
    text: 'Super recomendo! Equipe muito profissional, cumprem os prazos certinho e entregam produtos de ótima qualidade. Parabéns, pessoal! 👏',
    rating: 5,
    hasPhoto: false,
  },
  {
    name: 'Vinicius Damas',
    company: 'Cliente',
    image: '',
    text: 'Galera comprometida em entregar o projeto com qualidade e no prazo (dado o desafio que passei pra eles rsrs). Além disso o atendimento foi muito bom do início ao fim. Voltaria a fazer negócio!',
    rating: 5,
    hasPhoto: false,
  },
];

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="section-padding bg-gradient-to-br from-secondary-200 to-secondary-100 relative overflow-hidden">
      {/* Decorative Elements */}
      <motion.div
        className="absolute -top-20 -left-20 w-96 h-96 bg-primary-300 rounded-full blur-3xl opacity-20"
        animate={{ scale: [1, 1.3, 1], x: [0, 50, 0] }}
        transition={{ repeat: Infinity, duration: 15 }}
      />
      <motion.div
        className="absolute -bottom-20 -right-20 w-96 h-96 bg-primary-400 rounded-full blur-3xl opacity-20"
        animate={{ scale: [1, 1.2, 1], x: [0, -50, 0] }}
        transition={{ repeat: Infinity, duration: 20 }}
      />

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          {/* Left Side - Title */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-6"
          >
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">
              Depoimentos
            </span>
            <h2 className="text-dark">
              Mais de <span className="text-primary-500">14 mil pessoas</span>
              <br />
              confiam em nosso trabalho
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Veja o que nossos clientes têm a dizer sobre a qualidade e dedicação
              da Arte Verde Jardins.
            </p>

            {/* Google Rating */}
            <div className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-lg">
              <div>
                <p className="text-4xl font-bold text-dark">5.0</p>
                <div className="flex gap-1 my-2">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400" size={20} />
                  ))}
                </div>
                <p className="text-sm text-gray-600">Avaliação no Google</p>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Testimonials Carousel */}
          <div className="lg:col-span-3 relative">
            <div className="relative min-h-[400px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl relative"
                >
                  {/* Quote Icon */}
                  <div className="absolute -top-4 -left-4 bg-primary-500 text-white p-4 rounded-full shadow-lg">
                    <FaQuoteLeft size={24} />
                  </div>

                  {/* Rating */}
                  <div className="flex gap-1 mb-6">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <FaStar key={i} className="text-yellow-400" size={20} />
                    ))}
                  </div>

                  {/* Text */}
                  <p className="text-gray-700 text-lg leading-relaxed mb-8 min-h-[150px]">
                    "{testimonials[currentIndex].text}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-4">
                    {testimonials[currentIndex].hasPhoto ? (
                      <img
                        src={testimonials[currentIndex].image}
                        alt={testimonials[currentIndex].name}
                        className="w-16 h-16 rounded-full object-cover shadow-md"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center shadow-md">
                        <FaUser className="text-primary-500 text-2xl" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-bold text-dark text-lg">
                        {testimonials[currentIndex].name}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {testimonials[currentIndex].company}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex gap-3 mt-6 justify-center lg:justify-end">
                <button
                  onClick={prev}
                  className="bg-white hover:bg-primary-500 hover:text-white text-dark p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                  aria-label="Previous testimonial"
                >
                  <FaChevronLeft size={20} />
                </button>
                <button
                  onClick={next}
                  className="bg-white hover:bg-primary-500 hover:text-white text-dark p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                  aria-label="Next testimonial"
                >
                  <FaChevronRight size={20} />
                </button>
              </div>

              {/* Indicators */}
              <div className="flex gap-2 mt-4 justify-center lg:justify-end">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`transition-all duration-300 rounded-full ${
                      index === currentIndex
                        ? 'w-8 bg-primary-500'
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    } h-2`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
